import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import React from "react";
import { Toaster } from "sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";

function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPath = React.useRef(pathname);
  React.useEffect(() => {
    // Only scroll to top when changing major section, not within same area
    const prev = prevPath.current;
    prevPath.current = pathname;
    const prevSection = prev.split('/')[1];
    const newSection = pathname.split('/')[1];
    if (prevSection !== newSection) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import FirstLoginPage from "./pages/auth/FirstLoginPage";
import PinSetupPage from "./pages/auth/PinSetupPage";
import PinGatePage from "./pages/auth/PinGatePage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBranding from "./pages/admin/AdminBranding";
import AdminTemplate from "./pages/admin/AdminTemplate";
import AdminClients from "./pages/admin/AdminClients";
import AdminClientDetail from "./pages/admin/AdminClientDetail";
import AdminClientsWorkspace from "./pages/admin/AdminClientsWorkspace";
import MirrorPage from "./pages/admin/MirrorPage";
import AdminManagers from "./pages/admin/AdminManagers";
import AdminOffices from "./pages/admin/AdminOffices";
import AdminStats from "./pages/admin/AdminStats";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminWithdrawals from "./pages/admin/AdminWithdrawals";
import AdminAnalysts from "./pages/admin/AdminAnalysts";
import AdminPlacements from "./pages/admin/AdminPlacements";
import AdminPlacementForm from "./pages/admin/AdminPlacementForm";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminSecurity from "./pages/admin/AdminSecurity";
import AdminSettings from "./pages/admin/AdminSettings";
import WhatsAppTemplates from "./pages/admin/WhatsAppTemplates";
import AdminNews from "./pages/admin/AdminNews";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminWalletRequests from "./pages/admin/AdminWalletRequests";
import AdminAdvances from "./pages/admin/AdminAdvances";
import AdminMandates from "./pages/admin/AdminMandates";
import AdminIBANPool from "./pages/admin/AdminIBANPool";
import AnalystSettings from "./pages/analyst/AnalystSettings";
import AnalystPayments from "./pages/analyst/AnalystPayments";
import AnalystMyRIBs from "./pages/analyst/AnalystMyRIBs";

// Client Pages
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientDashboardV2 from "./pages/client/v2/ClientDashboardV2";
import ClientDashboardV3 from "./pages/client/v3/ClientDashboardV3";
import ClientDashboardV4 from "./pages/client/v4/ClientDashboardV4";
import ClientPortfolios from "./pages/client/ClientPortfolios";
import ClientWallets from "./pages/client/ClientWallets";
import ClientNews from "./pages/client/ClientNews";
import InvestmentSimulator from "./pages/client/InvestmentSimulator";
import ClientProducts from "./pages/client/ClientProducts";
import ClientProductDetail from "./pages/client/ClientProductDetail";
import ClientPlacements from "./pages/client/ClientPlacements";
import ClientPlacementCategory from "./pages/client/ClientPlacementCategory";
import ClientPlacementDetail from "./pages/client/ClientPlacementDetail";
import ClientMyInvestments from "./pages/client/ClientMyInvestments";
import ClientContracts from "./pages/client/ClientContracts";
import ClientTrading from "./pages/client/ClientTrading";
import ClientTransactions from "./pages/client/ClientTransactions";
import ClientDocuments from "./pages/client/ClientDocuments";
import ClientProfile from "./pages/client/ClientProfile";
import ClientCryptoBuy from "./pages/client/ClientCryptoBuy";
import ClientCryptoPortfolio from "./pages/client/ClientCryptoPortfolio";
import ClientStockPortfolio from "./pages/client/ClientStockPortfolio";
import CompareProducts from "./pages/client/CompareProducts";
import TradingRoom from "./pages/client/TradingRoom";

// Common Pages
import MessagesPage from "./pages/common/MessagesPage";
import NotificationsPage from "./pages/common/NotificationsPage";

// Layout
import ProtectedRoute from "./components/ProtectedRoute";
import PinGuard from "./components/PinGuard";
import SetupWizard from "./pages/SetupWizard";
import { useState, useEffect } from "react";
import axios from "axios";

// Wrapper to select V1, V2 or V3 dashboard based on platform setting
function ClientDashboardSelector() {
  const [version, setVersion] = useState(null);
  useEffect(() => {
    axios.get(`${API}/settings/platform`, { timeout: 10000 }).then(res => {
      setVersion(res.data?.design_version || 'v1');
    }).catch(() => setVersion('v1'));
  }, []);
  if (!version) return null;
  if (version === 'v4') return <ClientDashboardV4 />;
  if (version === 'v3') return <ClientDashboardV3 />;
  if (version === 'v2') return <ClientDashboardV2 />;
  return <ClientDashboard />;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AppContent() {
  const [needsSetup, setNeedsSetup] = useState(null); // null=loading, true=wizard, false=app
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let retries = 0;
    const MAX_RETRIES = 3;
    const checkSetup = () => {
      axios.get(`${API}/setup/status`, { timeout: 8000 }).then(res => {
        setNeedsSetup(res.data.needs_setup);
        setLoadError(false);
      }).catch(() => {
        retries++;
        if (retries >= MAX_RETRIES) {
          setLoadError(true);
        } else {
          setTimeout(checkSetup, 2000);
        }
      });
    };
    checkSetup();
  }, []);

  const handleRetry = () => {
    setLoadError(false);
    setNeedsSetup(null);
    axios.get(`${API}/setup/status`, { timeout: 10000 }).then(res => {
      setNeedsSetup(res.data.needs_setup);
    }).catch(() => {
      setLoadError(true);
    });
  };

  if (needsSetup === null || loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a1628' }}>
        <div className="text-center text-white space-y-3">
          {loadError ? (
            <>
              <p className="text-sm text-muted-foreground">Connexion au serveur impossible</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ backgroundColor: '#10b981', color: 'white' }}
              >
                Réessayer
              </button>
            </>
          ) : (
            <>
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground">Chargement...</p>
            </>
          )}
        </div>
      </div>
    );
  }
  if (needsSetup) return <SetupWizard onComplete={() => setNeedsSetup(false)} />;

  return (
    <>
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            color: 'hsl(var(--foreground))'
          }
        }}
      />
      <PWAInstallPrompt />
      <Routes>
                {/* PIN Gate - No guard needed, this IS the gate */}
                <Route path="/" element={<PinGatePage />} />
                <Route path="/pin" element={<PinGatePage />} />

                {/* All routes below require PIN verification */}
                <Route element={<PinGuard />}>
                  {/* Auth Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/first-login" element={<FirstLoginPage />} />
                  <Route path="/pin-setup" element={<PinSetupPage />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/branding" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops"]}><AdminBranding /></ProtectedRoute>} />
                  <Route path="/admin/template" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops"]}><AdminTemplate /></ProtectedRoute>} />
                  <Route path="/admin/clients" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><AdminClientsWorkspace /></ProtectedRoute>} />
                  <Route path="/admin/clients/:clientId" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><AdminClientsWorkspace /></ProtectedRoute>} />
                  <Route path="/mirror/:clientId" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><MirrorPage /></ProtectedRoute>} />
                  <Route path="/admin/managers" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminManagers /></ProtectedRoute>} />
                  <Route path="/admin/offices" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops"]}><AdminOffices /></ProtectedRoute>} />
                  <Route path="/admin/statistics" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminStats /></ProtectedRoute>} />
                  <Route path="/admin/transactions" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><AdminTransactions /></ProtectedRoute>} />
                  <Route path="/admin/withdrawals" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><AdminWithdrawals /></ProtectedRoute>} />
                  <Route path="/admin/analysts" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminAnalysts /></ProtectedRoute>} />
                  <Route path="/admin/placements" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminPlacements /></ProtectedRoute>} />
                  <Route path="/admin/placements/new" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminPlacementForm /></ProtectedRoute>} />
                  <Route path="/admin/placements/:productId" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminPlacementForm /></ProtectedRoute>} />
                  <Route path="/admin/audit" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminAudit /></ProtectedRoute>} />
                  <Route path="/admin/security" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminSecurity /></ProtectedRoute>} />
                  <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminSettings /></ProtectedRoute>} />
                  <Route path="/admin/whatsapp-templates" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops"]}><WhatsAppTemplates /></ProtectedRoute>} />
                  <Route path="/admin/news" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminNews /></ProtectedRoute>} />
                  <Route path="/admin/payments" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminPayments /></ProtectedRoute>} />
                  <Route path="/admin/wallet-requests" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><AdminWalletRequests /></ProtectedRoute>} />
                  <Route path="/admin/advances" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><AdminAdvances /></ProtectedRoute>} />
                  <Route path="/admin/mandates" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau", "analyst"]}><AdminMandates /></ProtectedRoute>} />
                  <Route path="/admin/iban-pool" element={<ProtectedRoute allowedRoles={["super_admin", "admin_ops", "bureau"]}><AdminIBANPool /></ProtectedRoute>} />
                  
                  {/* Analyst Routes */}
                  <Route path="/analyst" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/analyst/dashboard" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/analyst/clients" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminClientsWorkspace /></ProtectedRoute>} />
                  <Route path="/analyst/clients/:clientId" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminClientsWorkspace /></ProtectedRoute>} />
                  <Route path="/analyst/whatsapp-templates" element={<ProtectedRoute allowedRoles={["analyst"]}><WhatsAppTemplates /></ProtectedRoute>} />
                  <Route path="/analyst/placements" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminPlacements /></ProtectedRoute>} />
                  <Route path="/analyst/placements/new" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminPlacementForm /></ProtectedRoute>} />
                  <Route path="/analyst/placements/:productId" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminPlacementForm /></ProtectedRoute>} />
                  <Route path="/analyst/news" element={<ProtectedRoute allowedRoles={["analyst"]}><AdminNews /></ProtectedRoute>} />
                  <Route path="/analyst/payments" element={<ProtectedRoute allowedRoles={["analyst"]}><AnalystPayments /></ProtectedRoute>} />
                  <Route path="/analyst/my-ribs" element={<ProtectedRoute allowedRoles={["analyst"]}><AnalystMyRIBs /></ProtectedRoute>} />
                  <Route path="/analyst/settings" element={<ProtectedRoute allowedRoles={["analyst"]}><AnalystSettings /></ProtectedRoute>} />
                  
                  {/* Client Routes */}
                  <Route path="/client" element={<ProtectedRoute allowedRoles={["client"]}><ClientDashboardSelector /></ProtectedRoute>} />
                  <Route path="/client/dashboard" element={<ProtectedRoute allowedRoles={["client"]}><ClientDashboardSelector /></ProtectedRoute>} />
                  <Route path="/client/news" element={<ProtectedRoute allowedRoles={["client"]}><ClientNews /></ProtectedRoute>} />
                  <Route path="/client/portfolios" element={<ProtectedRoute allowedRoles={["client"]}><ClientPortfolios /></ProtectedRoute>} />
                  <Route path="/client/wallets" element={<ProtectedRoute allowedRoles={["client"]}><ClientWallets /></ProtectedRoute>} />
                  <Route path="/client/simulator" element={<ProtectedRoute allowedRoles={["client"]}><InvestmentSimulator /></ProtectedRoute>} />
                  <Route path="/client/products" element={<ProtectedRoute allowedRoles={["client"]}><ClientProducts /></ProtectedRoute>} />
                  <Route path="/client/products/:productId" element={<ProtectedRoute allowedRoles={["client"]}><ClientProductDetail /></ProtectedRoute>} />
                  <Route path="/client/placements" element={<ProtectedRoute allowedRoles={["client"]}><ClientPlacements /></ProtectedRoute>} />
                  <Route path="/client/placements/:categoryId" element={<ProtectedRoute allowedRoles={["client"]}><ClientPlacementCategory /></ProtectedRoute>} />
                  <Route path="/client/placements/product/:productId" element={<ProtectedRoute allowedRoles={["client"]}><ClientPlacementDetail /></ProtectedRoute>} />
                  <Route path="/client/my-investments" element={<ProtectedRoute allowedRoles={["client"]}><ClientMyInvestments /></ProtectedRoute>} />
                  <Route path="/client/contracts" element={<ProtectedRoute allowedRoles={["client"]}><ClientContracts /></ProtectedRoute>} />
                  <Route path="/client/trading" element={<ProtectedRoute allowedRoles={["client"]}><ClientTrading /></ProtectedRoute>} />
                  <Route path="/client/arbitrage" element={<ProtectedRoute allowedRoles={["client"]}><TradingRoom marketType="arbitrage" /></ProtectedRoute>} />
                  <Route path="/client/bourse" element={<ProtectedRoute allowedRoles={["client"]}><TradingRoom marketType="bourse" /></ProtectedRoute>} />
                  <Route path="/client/crypto" element={<ProtectedRoute allowedRoles={["client"]}><TradingRoom marketType="crypto" /></ProtectedRoute>} />
                  <Route path="/client/crypto-buy" element={<ProtectedRoute allowedRoles={["client"]}><ClientCryptoBuy /></ProtectedRoute>} />
                  <Route path="/client/crypto-portfolio" element={<ProtectedRoute allowedRoles={["client"]}><ClientCryptoPortfolio /></ProtectedRoute>} />
                  <Route path="/client/stock-portfolio" element={<ProtectedRoute allowedRoles={["client"]}><ClientStockPortfolio /></ProtectedRoute>} />
                  <Route path="/client/transactions" element={<ProtectedRoute allowedRoles={["client"]}><ClientTransactions /></ProtectedRoute>} />
                  <Route path="/client/documents" element={<ProtectedRoute allowedRoles={["client"]}><ClientDocuments /></ProtectedRoute>} />
                  <Route path="/client/profile" element={<ProtectedRoute allowedRoles={["client"]}><ClientProfile /></ProtectedRoute>} />
                  <Route path="/client/compare" element={<ProtectedRoute allowedRoles={["client"]}><CompareProducts /></ProtectedRoute>} />
                  
                  {/* Common Routes */}
                  <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                </Route>
                
                {/* Default Redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
    </>
  );
}

function ErrorBoundaryWrapper({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundaryWrapper>
        <LanguageProvider>
          <ThemeProvider>
            <SettingsProvider>
              <AuthProvider>
                <AppContent />
              </AuthProvider>
            </SettingsProvider>
          </ThemeProvider>
        </LanguageProvider>
      </ErrorBoundaryWrapper>
    </BrowserRouter>
  );
}

export default App;
